import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiRequest, queryClient } from '@/lib/queryClient';
import type { MilestoneType } from '@/components/milestones/MilestoneCelebration';

interface UserMilestone {
  id: string;
  userId: string;
  organizationId: string;
  milestoneType: MilestoneType;
  achievedAt: string;
  metadata?: {
    count?: number;
    contextId?: string;
    contextType?: string;
  };
}

interface AchieveMilestoneParams {
  milestoneType: MilestoneType;
  metadata?: {
    count?: number;
    contextId?: string;
    contextType?: string;
  };
}

interface MilestoneContextValue {
  milestones: UserMilestone[];
  isLoading: boolean;
  hasMilestone: (milestoneType: MilestoneType) => boolean;
  achieveMilestone: (params: AchieveMilestoneParams) => Promise<void>;
  celebrationMilestone: MilestoneType | null;
  dismissCelebration: () => void;
  isAchieving: boolean;
}

const MilestoneContext = createContext<MilestoneContextValue | undefined>(undefined);

export function MilestoneProvider({ children }: { children: ReactNode }) {
  const [celebrationMilestone, setCelebrationMilestone] = useState<MilestoneType | null>(null);

  // Fetch user's achieved milestones - only when authenticated
  const hasToken = !!localStorage.getItem('token');
  const { data: milestones = [], isLoading } = useQuery<UserMilestone[]>({
    queryKey: ['/api/milestones'],
    enabled: hasToken, // Only fetch if user is authenticated
  });

  // Mutation to achieve a milestone
  const achieveMilestoneMutation = useMutation({
    mutationFn: async (params: AchieveMilestoneParams) => {
      return await apiRequest('POST', '/api/milestones', params);
    },
    onSuccess: (data: UserMilestone, variables) => {
      // Invalidate milestones cache
      queryClient.invalidateQueries({ queryKey: ['/api/milestones'] });
      
      // Show celebration
      setCelebrationMilestone(variables.milestoneType);
    },
  });

  // Check if a milestone has been achieved
  const hasMilestone = useCallback((milestoneType: MilestoneType): boolean => {
    return milestones.some(m => m.milestoneType === milestoneType);
  }, [milestones]);

  // Attempt to achieve a milestone (will only succeed if not already achieved)
  const achieveMilestone = useCallback(async (params: AchieveMilestoneParams) => {
    // Don't attempt if already achieved
    if (hasMilestone(params.milestoneType)) {
      return;
    }

    try {
      await achieveMilestoneMutation.mutateAsync(params);
    } catch (error) {
      // Silently fail if milestone already exists (race condition)
      console.log('Milestone may already be achieved:', error);
    }
  }, [hasMilestone, achieveMilestoneMutation]);

  // Dismiss the celebration
  const dismissCelebration = useCallback(() => {
    setCelebrationMilestone(null);
  }, []);

  const value: MilestoneContextValue = {
    milestones,
    isLoading,
    hasMilestone,
    achieveMilestone,
    celebrationMilestone,
    dismissCelebration,
    isAchieving: achieveMilestoneMutation.isPending,
  };

  return (
    <MilestoneContext.Provider value={value}>
      {children}
    </MilestoneContext.Provider>
  );
}

export function useMilestoneContext() {
  const context = useContext(MilestoneContext);
  if (!context) {
    throw new Error('useMilestoneContext must be used within MilestoneProvider');
  }
  return context;
}
